import { selectedNodeAtom } from "@/components/ui/TextMenu/store";
import { updateElemental } from "@/lib/utils";
import { type Channel, channelAtom, CHANNELS, type ChannelType } from "@/store";
import type { ElementalChannelNode, ElementalNode } from "@/types/elemental.types";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isTemplateLoadingAtom, type MessageRouting } from "../../Providers/store";
import { templateEditorContentAtom, pendingAutoSaveAtom, templateEditorAtom } from "../store";
import type { TemplateEditorProps } from "../TemplateEditor";
import { defaultEmailContent } from "./Email";
import { defaultInboxContent } from "./Inbox";
import { defaultPushContent } from "./Push";
import { defaultSlackContent } from "./Slack";
import { defaultSMSContent } from "./SMS";
import { defaultMSTeamsContent } from "./MSTeams";

const channelDefaults: Record<
  ChannelType,
  { elements: ElementalNode[]; raw?: { title?: string; text?: string } }
> = {
  sms: { elements: defaultSMSContent },
  push: { elements: defaultPushContent },
  inbox: { elements: defaultInboxContent },
  slack: { elements: defaultSlackContent },
  email: { elements: defaultEmailContent },
  msteams: { elements: defaultMSTeamsContent },
};

export const getChannelDefaults = (type: ChannelType) =>
  channelDefaults[type] ?? channelDefaults["email"];

// Helper function to resolve channels with priority: routing.channels > channels prop
const resolveChannels = (routing?: MessageRouting, channelsProp?: ChannelType[]): ChannelType[] => {
  // If routing.channels exists, use it (top priority)
  if (routing?.channels && routing.channels.length > 0) {
    // Filter out any non-string routing channels and convert to ChannelType[]
    const validChannels = routing.channels.filter(
      (channel): channel is string => typeof channel === "string"
    ) as ChannelType[];

    // If we have valid channels after filtering, use them
    if (validChannels.length > 0) {
      return validChannels;
    }
  }

  // Fallback to channels prop or default
  return channelsProp ?? ["email"];
};

export const useChannels = ({
  channels = ["email"],
  routing,
}: {
  /** @deprecated Use routing.channels instead. Will be removed in a future version. */
  channels?: ChannelType[];
  routing?: TemplateEditorProps["routing"];
}): {
  enabledChannels: Channel[];
  disabledChannels: Channel[];
  channel: ChannelType;
  channelOptions: Channel[];
  setChannel: (channelType: ChannelType) => void;
  addChannel: (channelType: ChannelType) => void;
  removeChannel: (channelToRemove: ChannelType) => void;
} => {
  const [enabledChannels, setEnabledChannels] = useState<Channel[]>([]);
  const templateEditorContent = useAtomValue(templateEditorContentAtom);
  const setTemplateEditorContent = useSetAtom(templateEditorContentAtom);
  const setPendingAutoSave = useSetAtom(pendingAutoSaveAtom);
  const [channel, _setChannel] = useAtom(channelAtom);
  const setSelectedNode = useSetAtom(selectedNodeAtom);
  const setTemplateEditor = useSetAtom(templateEditorAtom);
  const isTemplateLoading = useAtomValue(isTemplateLoadingAtom);

  // Wrap setChannel to clear cross-channel state synchronously on channel switch,
  // preventing stale sidebar forms from the previous channel.
  const setChannel = useCallback(
    (newChannel: ChannelType) => {
      if (newChannel !== channel) {
        setSelectedNode(null);
        setTemplateEditor(null);
      }
      _setChannel(newChannel);
    },
    [_setChannel, setSelectedNode, setTemplateEditor, channel]
  );

  // Resolve channels with priority: routing.channels > channels prop
  const resolvedChannels = resolveChannels(routing, channels);

  // Use ref to store enabled channels to avoid dependency issues
  const enabledChannelsRef = useRef(resolvedChannels);
  enabledChannelsRef.current = resolvedChannels;

  // Handle initial channel selection when there's no content
  useEffect(() => {
    if (isTemplateLoading) return;
    if (templateEditorContent?.elements) return;

    const currentEnabledChannels = enabledChannelsRef.current;
    const availableChannels = currentEnabledChannels
      .map((channelValue) => CHANNELS.find((c) => c.value === channelValue))
      .filter((channel): channel is Channel => Boolean(channel));

    setEnabledChannels(availableChannels);

    if (availableChannels.length > 0 && !channel) {
      setChannel(availableChannels[0].value);
    }
  }, [templateEditorContent, isTemplateLoading, setChannel, channel]);

  // Recalculate enabled channels based on template content
  useEffect(() => {
    if (isTemplateLoading) return;
    if (!templateEditorContent?.elements) return;

    const currentEnabledChannels = enabledChannelsRef.current;

    const existingChannelNames = templateEditorContent.elements
      .filter((el): el is ElementalChannelNode => el.type === "channel")
      .map((el) => el.channel)
      .filter((channelName): channelName is ChannelType =>
        currentEnabledChannels.includes(channelName as ChannelType)
      );

    const existingChannels = currentEnabledChannels
      .filter((channelValue) => existingChannelNames.includes(channelValue))
      .map((channelValue) => CHANNELS.find((c) => c.value === channelValue))
      .filter((channel): channel is Channel => Boolean(channel));

    setEnabledChannels(existingChannels);
  }, [templateEditorContent, isTemplateLoading]);

  useEffect(() => {
    if (isTemplateLoading) return;
    if (enabledChannels.length === 0) return;

    const currentChannelExists = enabledChannels.some((c) => c.value === channel);

    const channelExistsInContent = templateEditorContent?.elements?.some(
      (el) => el.type === "channel" && (el as ElementalChannelNode).channel === channel
    );

    if (!currentChannelExists && !channelExistsInContent) {
      setChannel(enabledChannels[0].value);
    }
  }, [enabledChannels, channel, setChannel, isTemplateLoading, templateEditorContent]);

  const disabledChannels: Channel[] = useMemo(
    () =>
      resolvedChannels
        ?.filter(
          (channelValue) =>
            !enabledChannels.some((existingChannel) => existingChannel.value === channelValue)
        )
        .map((channelValue) => CHANNELS.find((c) => c.value === channelValue))
        .filter((channel): channel is Channel => Boolean(channel)) || [],
    [enabledChannels, resolvedChannels]
  );

  const addChannel = useCallback(
    (channelType: ChannelType) => {
      let defaultElements: ElementalNode[] = [];
      let channelRaw: { title?: string; text?: string } | undefined = undefined;

      const { elements: defaultElementsLookup, raw: channelRawLookup } =
        getChannelDefaults(channelType);
      defaultElements = defaultElementsLookup;
      channelRaw = channelRawLookup;

      // Handle new templates with null content
      if (!templateEditorContent) {
        const channelElements = [];

        // Add existing channels first
        for (const existingChannel of enabledChannels) {
          const { elements: existingDefaultElements, raw: existingChannelRaw } = getChannelDefaults(
            existingChannel.value as ChannelType
          );

          const channelElement: ElementalChannelNode = {
            type: "channel" as const,
            channel: existingChannel.value,
            elements: existingDefaultElements,
          };

          if (existingChannelRaw) {
            channelElement.raw = existingChannelRaw;
          }

          channelElements.push(channelElement);
        }

        // Add the new channel
        const newChannelElement: ElementalChannelNode = {
          type: "channel" as const,
          channel: channelType,
          elements: defaultElements,
        };

        if (channelRaw) {
          newChannelElement.raw = channelRaw;
        }

        channelElements.push(newChannelElement);

        const initialContent = {
          version: "2022-01-01" as const,
          elements: channelElements,
        };

        setTemplateEditorContent(initialContent);
        setPendingAutoSave(initialContent);
        setChannel(channelType);
        return;
      }

      const updateOptions = {
        elements: defaultElements,
        channel: channelRaw
          ? {
              channel: channelType,
              raw: channelRaw,
            }
          : channelType,
      };

      const updatedContent = updateElemental(templateEditorContent, updateOptions);

      setTemplateEditorContent(updatedContent);
      setPendingAutoSave(updatedContent);
      setChannel(channelType);
    },
    [
      templateEditorContent,
      setTemplateEditorContent,
      setPendingAutoSave,
      setChannel,
      enabledChannels,
    ]
  );

  const removeChannel = useCallback(
    (channelToRemove: ChannelType) => {
      if (!templateEditorContent) return;

      // Filter out the channel elements that match the channel to remove
      const filteredElements = templateEditorContent.elements.filter((el) => {
        if (el.type === "channel") {
          // Cast to channel element to access the channel property
          const channelElement = el as ElementalNode & { channel: string };
          return channelElement.channel !== channelToRemove;
        }
        return true; // Keep non-channel elements
      });

      const updatedContent = {
        ...templateEditorContent,
        elements: filteredElements,
      };

      setTemplateEditorContent(updatedContent);
      setPendingAutoSave(updatedContent);

      setSelectedNode(null);

      // If we're removing the currently active channel, switch to the first remaining channel
      const remainingChannels = enabledChannels.filter((c) => c.value !== channelToRemove);
      if (channel === channelToRemove && remainingChannels.length > 0) {
        setChannel(remainingChannels[0].value);
      }
    },
    [
      templateEditorContent,
      setTemplateEditorContent,
      setPendingAutoSave,
      enabledChannels,
      channel,
      setChannel,
      setSelectedNode,
    ]
  );

  const channelOptions = useMemo(
    () => [...enabledChannels, ...disabledChannels],
    [enabledChannels, disabledChannels]
  );

  return {
    enabledChannels,
    disabledChannels,
    channel,
    channelOptions,
    setChannel,
    addChannel,
    removeChannel,
  };
};
